import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductImageUploader } from "@/components/common/ProductImageUploader";
import { ProductCostPanel } from "@/components/product/ProductCostPanel";
import { ProductInventoryPanel } from "@/components/product/ProductInventoryPanel";
import { PRODUCT_CATEGORIES } from "@/lib/productCategories";

const EMPTY = {
  product_type: "product", sku: "", ean_gtin: "", manufacturer: "", category: "", vehicle_make: "", vehicle_model: "", supplier_article_number: "", search_aliases: "",
  name_de: "", name_en: "", description_de: "", description_en: "", unit: "Stk", vat_class: "standard",
  cost_price: "0", selling_price: "0", is_active: true,
};

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const readOnly = !can("products:write");
  const canManageImage = can("products:write") || can("inventory:write");

  const [form, setForm] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => (await api.get(`/products/${id}`)).data,
    enabled: isEdit,
  });

  useEffect(() => { if (data) setForm({ ...EMPTY, ...data }); }, [data]);
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const margin = (() => {
    const c = parseFloat(form.cost_price) || 0;
    const s = parseFloat(form.selling_price) || 0;
    if (s === 0) return null;
    return (((s - c) / s) * 100).toFixed(1);
  })();

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, cost_price: String(form.cost_price || "0"), selling_price: String(form.selling_price || "0") };
      if (isEdit) { await api.put(`/products/${id}`, payload); toast.success(t("products.updated_success")); }
      else { await api.post("/products", payload); toast.success(t("products.created_success")); }
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["products"] });
      navigate("/products");
    } catch (err) {
      toast.error(apiErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div>
      <PageHeader
        title={isEdit ? t("products.edit") : t("products.new")}
        actions={<Button variant="outline" onClick={() => navigate("/products")} data-testid="product-back"><ArrowLeft className="h-4 w-4" />{t("common.back")}</Button>}
      />
      <form onSubmit={submit} className="space-y-6">
        <Card className="p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("products.type")}</Label>
              <Select value={form.product_type} onValueChange={(v) => set("product_type", v)} disabled={readOnly}>
                <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">{t("products.product")}</SelectItem>
                  <SelectItem value="service">{t("products.service")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.sku")} *</Label>
              <Input required value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} disabled={readOnly} data-testid="product-sku-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.name_de")} *</Label>
              <Input required value={form.name_de} onChange={(e) => set("name_de", e.target.value)} disabled={readOnly} data-testid="product-namede-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.name_en")}</Label>
              <Input value={form.name_en || ""} onChange={(e) => set("name_en", e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.ean")}</Label>
              <Input value={form.ean_gtin || ""} onChange={(e) => set("ean_gtin", e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.manufacturer")}</Label>
              <Input value={form.manufacturer || ""} onChange={(e) => set("manufacturer", e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.category")}</Label>
              <Select value={form.category || "none"} onValueChange={(v) => set("category", v === "none" ? "" : v)} disabled={readOnly}>
                <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("products.no_category")}</SelectItem>
                  {PRODUCT_CATEGORIES.map((category) => <SelectItem key={category} value={category}>{t(`products.categories.${category}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.vehicle_make")}</Label>
              <Input value={form.vehicle_make || ""} onChange={(e) => set("vehicle_make", e.target.value)} disabled={readOnly} placeholder="z. B. Volkswagen" data-testid="product-vehicle-make-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.vehicle_model")}</Label>
              <Input value={form.vehicle_model || ""} onChange={(e) => set("vehicle_model", e.target.value)} disabled={readOnly} placeholder="z. B. Golf VII" data-testid="product-vehicle-model-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.supplier_article")}</Label>
              <Input value={form.supplier_article_number || ""} onChange={(e) => set("supplier_article_number", e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.search_aliases")}</Label>
              <Input value={form.search_aliases || ""} onChange={(e) => set("search_aliases", e.target.value)} disabled={readOnly} placeholder={t("products.search_aliases_hint")} data-testid="product-aliases-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.unit")}</Label>
              <Input value={form.unit} onChange={(e) => set("unit", e.target.value)} disabled={readOnly} />
            </div>
          </div>
        </Card>

        {isEdit && (
          <Card className="p-6" data-testid="product-image-card">
            <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("images.product_image")}</h3>
            <ProductImageUploader productId={id} imageUrl={data?.image_doc_url} canManage={canManageImage}
                                  onChanged={() => refetch()} />
          </Card>
        )}

        {isEdit && <ProductCostPanel productId={id} canManage={canManageImage} />}

        {isEdit && <ProductInventoryPanel productId={id} canManage={canManageImage} />}

        <Card className="p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="space-y-1.5">
              <Label>{t("products.vat_class")}</Label>
              <Select value={form.vat_class} onValueChange={(v) => set("vat_class", v)} disabled={readOnly}>
                <SelectTrigger data-testid="product-vat-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">{t("products.vat_standard")}</SelectItem>
                  <SelectItem value="reduced">{t("products.vat_reduced")}</SelectItem>
                  <SelectItem value="zero">{t("products.vat_zero")}</SelectItem>
                  <SelectItem value="exempt">{t("products.vat_exempt")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.cost_price")}</Label>
              <Input type="number" step="0.0001" min="0" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} disabled={readOnly} className="tabular-nums" data-testid="product-cost-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.selling_price")}</Label>
              <Input type="number" step="0.0001" min="0" value={form.selling_price} onChange={(e) => set("selling_price", e.target.value)} disabled={readOnly} className="tabular-nums" data-testid="product-price-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.margin")}</Label>
              <div className="flex h-9 items-center rounded-md border border-input bg-slate-50 px-3 text-sm tabular-nums dark:bg-slate-800" data-testid="product-margin">{margin != null ? `${margin}%` : "—"}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("products.description_de")}</Label>
              <Textarea value={form.description_de || ""} onChange={(e) => set("description_de", e.target.value)} disabled={readOnly} rows={3} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.description_en")}</Label>
              <Textarea value={form.description_en || ""} onChange={(e) => set("description_en", e.target.value)} disabled={readOnly} rows={3} />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} disabled={readOnly} id="p-active" data-testid="product-active-switch" />
            <Label htmlFor="p-active">{t("common.active")}</Label>
          </div>
        </Card>

        {!readOnly && (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/products")}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={saving} data-testid="product-save-button">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("common.save")}</Button>
          </div>
        )}
      </form>
    </div>
  );
}
